import Parameter from "./parameter";
import ModuleCard from "./module-card";
import Msg from "./message";
import FieldPromote from "./field-promote";
import Field from "./field";
import Entity from "./entity";
import Documents from "./documents";
import DocumentValue from "./document-value";
import { clone } from "../utils";
import ISchedulerEvent from "../interface/scheduler-event";
import ISchedulerDataset from "../interface/scheduler-dataset";
import ISchedulerData from "../interface/scheduler-data";
import IScheduler from "../interface/scheduler";
import IPlanningFilter from "../interface/planning-filter";
import IPlanningColorValue from "../interface/planning-color-value";
import IPlanningColor from "../interface/planning-color";
import IModuleCard from "../interface/module-card";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IDocument from "../interface/document";
import ICardScheduler from "../interface/card-scheduler";
import Global from "../global";
import ParamStorage from "../enum/param-storage";
import Message from "../enum/message";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";
import DateType from "../enum/date-type";
import CardType from "../enum/card-type";
import DefaultConfig from "../default-config";
import ParameterStorage from "../class/parameter-storage";

class SchedulerSingleton {

    public getAll(): IScheduler[] {
        return this.getStore().getAll();
    }

    public get(id: number): IScheduler | null {
        return this.getStore().get(id);
    }

    public async create(scheduler: IScheduler): Promise<IScheduler> {
        if (!scheduler.colors) {
            scheduler.colors = [];
        }
        await this.processFields(scheduler);
        return await this.getStore().create(scheduler);
    }

    public async update(scheduler: IScheduler): Promise<IScheduler> {
        const original: IScheduler | null = this.get(scheduler.id as number);
        if (original) {
            await this.processFields(scheduler);
            const colors: number[] = [];
            scheduler.datasets.forEach((dataset: ISchedulerDataset) => colors.push(dataset.owner));
            scheduler.colors = original.colors.filter((color: IPlanningColor) => colors.includes(color.fieldId));
            return await this.getStore().update(scheduler);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async delete(id: number, deleteCard: boolean = true): Promise<number> {
        if (deleteCard) {
            const cards: IModuleCard[] = ModuleCard.getAll();
            for (let i = 0; i < cards.length; i++) {
                const card: IModuleCard = cards[i];
                if (card.type === CardType.Scheduler && (card as ICardScheduler).schedulerId === id) {
                    await ModuleCard.delete(card.id as number);
                }
            }
        }
        return this.getStore().delete(id);
    }

    public async rename(id: number, name: string): Promise<IScheduler | null> {
        const scheduler: IScheduler | null = this.getStore().get(id);
        if (scheduler) {
            scheduler.name = name;
            await this.getStore().update(scheduler);
            return this.getStore().get(id);
        }
        return null;
    }

    public async clone(id: number): Promise<IScheduler> {
        const origin: IScheduler | null = this.get(id);
        if (origin) {
            const scheduler: IScheduler = clone(origin);
            delete scheduler.id;
            return await this.getStore().create(scheduler);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async getData(id: number, useCache: boolean = true): Promise<ISchedulerData> {
        const scheduler: IScheduler | null = this.get(id);
        /* Validate schedule */
        if (scheduler) {
            const events: ISchedulerEvent[] = [];
            const ownerFilters: IPlanningFilter[] = [];
            const consumerFilters: IPlanningFilter[] = [];
            const colors: IPlanningColorValue[] = [];
            for (let i = 0; i < scheduler.datasets.length; i++) {
                const dataset: ISchedulerDataset = scheduler.datasets[i];
                const entity: IEntity | null = Entity.get(dataset.entityId);
                const ownerField: IField | null = Field.get(dataset.owner);
                const consumerField: IField | null = Field.get(dataset.consumer);
                /* Validate dataset */
                if (entity && ownerField && consumerField) {
                    const documents: IDocument[] = await Documents.loadDocuments(dataset.entityId, undefined, useCache);
                    await Documents.loadRelatedDocs(entity, documents);
                    Documents.setLinkedFieldValues(entity, documents);
                    for (let j = 0; j < documents.length; j++) {
                        const doc: IDocument = documents[j];
                        const ownerId: number | null = DocumentValue.getPointerId(doc, ownerField);
                        const from: string = doc[`c${dataset.start}`] as string || '';
                        /* Validate event */
                        if (ownerId && from) {
                            const consumerId: number | null = DocumentValue.getPointerId(doc, consumerField);
                            const event: ISchedulerEvent = {
                                id: `${dataset.entityId},${doc.id}`,
                                entityId: dataset.entityId,
                                recordId: doc.id as number,
                                ownerName: doc[`d${dataset.owner}`] as string || '',
                                consumerName: doc[`d${dataset.consumer}`] as string || '',
                                from,
                                to: doc[`c${dataset.end}`] as string || '',
                                data: doc,
                                ownerId: `${dataset.owner},${ownerId}`,
                                consumerId: consumerId ? `${dataset.consumer},${consumerId}` : ''
                            };
                            if (!event.to) {
                                const time: number = dataset.defaultDuration * Global.MS_IN_MINUTE;
                                event.to = (new Date((new Date(event.from)).getTime() + time)).toISOString();
                            }
                            /* Add owner color to the list of data colors and not in schedule colors config */
                            if (!colors.find(c => c.id === event.ownerId)) {
                                const color: IPlanningColor | null = scheduler.colors.find((c: IPlanningColor) => {
                                    return c.value === ownerId && c.fieldId === dataset.owner;
                                }) || null;
                                colors.push({
                                    id: event.ownerId,
                                    fieldId: dataset.owner,
                                    color: color ? color.color : '',
                                    value: ownerId,
                                    name: event.ownerName
                                });
                            }
                            /* Add event owner to the list of owner filters */
                            if (!ownerFilters.find(f => f.id === event.ownerId)) {
                                ownerFilters.push({ id: event.ownerId, name: event.ownerName });
                            }
                            /* Add event consumer to the list of consumer filters */
                            if (event.consumerId && !consumerFilters.find(f => f.id === event.consumerId)) {
                                consumerFilters.push({ id: event.consumerId, name: event.consumerName });
                            }
                            events.push(event);
                        }
                    }
                }
            }
            return { ownerFilters, consumerFilters, colors, events };
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async setColors(id: number, colors: IPlanningColor[]): Promise<IScheduler> {
        const scheduler: IScheduler | null = this.get(id);
        if (scheduler) {
            scheduler.colors = colors;
            return await this.getStore().update(scheduler);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    /* Private Methods */

    private async processFields(scheduler: IScheduler): Promise<void> {
        for (let i = 0; i < scheduler.datasets.length; i++) {
            if (scheduler.datasets[i].start === -1) {
                const field: IField = await Field.create(scheduler.datasets[i].entityId, Msg.get(Message.fieldfrom));
                await FieldPromote.toDatetime(field.id, DefaultConfig[FieldType.datetime](DateType.DateTime));
                scheduler.datasets[i].start = field.id;
            }
            if (scheduler.datasets[i].end === -1) {
                const field: IField = await Field.create(scheduler.datasets[i].entityId, Msg.get(Message.fieldto));
                await FieldPromote.toDatetime(field.id, DefaultConfig[FieldType.datetime](DateType.DateTime));
                scheduler.datasets[i].end = field.id;
            }
        }
    }

    /* Private Methods - Parameter Storage */

    private getStore(): ParameterStorage<IScheduler> {
        return Parameter.get(ParamStorage.Schedulers);
    }

}

const Scheduler: SchedulerSingleton = new SchedulerSingleton();

export default Scheduler;